import React, { useEffect, useRef, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import useSearchBrands from '../../../query/useSearchBrands';

const SEARCH_DEBOUNCE_MS = 250;

export default function BrandRadarSearch({ onSelectBrand }) {
  const inputRef = useRef(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [cachedSearchBrands, setCachedSearchBrands] = useState([]);

  const normalizedSearchQuery = searchQuery.trim();
  const hasSearchQuery = normalizedSearchQuery.length > 0;
  const isDebouncing =
    hasSearchQuery && normalizedSearchQuery !== debouncedSearchQuery;
  const { data, loading } = useSearchBrands(debouncedSearchQuery);
  const hasSearchResponse = Boolean(data?.searchBrands);
  const searchBrands = hasSearchQuery
    ? data?.searchBrands ?? cachedSearchBrands
    : [];
  const showSearchResults =
    searchOpen &&
    hasSearchQuery &&
    (isDebouncing || loading || hasSearchResponse || searchBrands.length > 0);

  useEffect(() => {
    if (!hasSearchQuery) {
      setDebouncedSearchQuery('');
      return undefined;
    }

    const timeoutId = setTimeout(() => {
      setDebouncedSearchQuery(normalizedSearchQuery);
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timeoutId);
  }, [hasSearchQuery, normalizedSearchQuery]);

  useEffect(() => {
    if (!hasSearchQuery) {
      setCachedSearchBrands([]);
      return;
    }

    if (data?.searchBrands) {
      setCachedSearchBrands(data.searchBrands);
    }
  }, [data, hasSearchQuery]);

  const handleBlur = useCallback((event) => {
    if (!event.currentTarget.contains(event.relatedTarget)) {
      setSearchOpen(false);
    }
  }, []);

  const handleSelect = useCallback(
    (brandName) => {
      onSelectBrand(brandName);
      setSearchQuery('');
      setDebouncedSearchQuery('');
      setCachedSearchBrands([]);
      setSearchOpen(false);
      inputRef.current?.blur();
    },
    [onSelectBrand]
  );

  const handleInputChange = useCallback((event) => {
    setSearchOpen(true);
    setSearchQuery(event.target.value);
  }, []);

  return (
    <div
      className="BrandRadar-search"
      onFocus={() => setSearchOpen(true)}
      onBlur={handleBlur}
    >
      <span className="BrandRadar-search-icon">&#x2315;</span>
      <input
        ref={inputRef}
        type="text"
        aria-label="브랜드 검색"
        placeholder="Search all brands..."
        value={searchQuery}
        onChange={handleInputChange}
      />
      {(isDebouncing || loading) && searchQuery && (
        <span className="BrandRadar-search-loading">...</span>
      )}
      <div
        className={`BrandRadar-search-results ${
          showSearchResults ? 'is-open' : ''
        }`}
      >
        {searchBrands.map((brand) => (
          <button
            type="button"
            key={brand.id}
            className="BrandRadar-search-item"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => handleSelect(brand.name)}
          >
            <span className="BrandRadar-search-dot" />
            <span className="BrandRadar-search-name">{brand.name}</span>
            {brand.name_kr && (
              <span className="BrandRadar-search-name-kr">{brand.name_kr}</span>
            )}
          </button>
        ))}
        {searchBrands.length === 0 && (isDebouncing || loading) && (
          <div className="BrandRadar-search-status">검색 중...</div>
        )}
        {searchBrands.length === 0 &&
          !isDebouncing &&
          !loading &&
          hasSearchResponse && (
          <div className="BrandRadar-search-status">검색 결과가 없습니다</div>
        )}
      </div>
    </div>
  );
}

BrandRadarSearch.propTypes = {
  onSelectBrand: PropTypes.func.isRequired,
};
